/* eslint-disable jsx-a11y/anchor-is-valid */
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Textarea,
  TextInput,
} from "flowbite-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { HiOutlinePencilAlt, HiExternalLink } from "react-icons/hi";
import { toast } from "react-toastify";
import { updateTrainer } from "../../services/trainers";
import * as Sentry from "@sentry/react";
import FavoriteTable, { FavoriteType } from "./FavoriteTable";
import type { FavoriteItem } from "./FavoriteTable";
import FileUploadZone from "../media/FileUploadZone";
import UserSearchSelect from "../../components/UserSearchSelect";
import {
  getSubscriptionsByTrainer,
  type TrainerSubscription,
} from "../../services/trainerSubscriptions";

const EditTrainerModal = function (props: any) {
  const [isOpen, setOpen] = useState(false);
  const [updating, setUpdate] = useState(false);
  const [trainer, setTrainer]: any = useState({});
  const [favouriteVideos, setFavouriteVideos] = useState<FavoriteItem[]>([]);
  const [favouritePrograms, setFavouritePrograms] = useState<FavoriteItem[]>(
    [],
  );
  const [favouriteWorkouts, setFavouriteWorkouts] = useState<FavoriteItem[]>(
    [],
  );
  const [touched, setTouched] = useState<{ [key: string]: boolean }>({});
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [subscribers, setSubscribers] = useState<TrainerSubscription[]>([]);
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);

  const handleChange = (name: string, value: any) => {
    const data: any = {
      ...trainer,
      [name]: value,
    };
    setTrainer(data);
    setTouched({
      ...touched,
      [name]: true,
    });
  };

  const handleFavoritesChange = (type: string, items: FavoriteItem[]) => {
    if (type === FavoriteType.Videos) {
      setFavouriteVideos(items);
    } else if (type === FavoriteType.Workouts) {
      setFavouriteWorkouts(items);
    } else if (type === FavoriteType.Programs) {
      setFavouritePrograms(items);
    }
    setTouched({
      ...touched,
      [type]: true,
    });
  };

  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.trainer) {
      setTrainer({
        description: props.trainer.description,
        monthly_plays: props.trainer.monthly_plays,
        followers: props.trainer.total_followers,
        social_ig: props.trainer.social_ig,
        social_tt: props.trainer.social_tt,
        social_fb: props.trainer.social_fb,
        social_tw: props.trainer.social_tw,
        profile_image: props.trainer.profile_image,
        cover_image: props.trainer.cover_image,
        thumb_image: props.trainer.thumb_image,
        status: props.trainer.status,
        associated_user_id: props.trainer.associated_user_id,
        paywall_enabled: props.trainer.paywall_enabled,
        subscription_description: props.trainer.subscription_description,
        subscription_amount_cents: props.trainer.subscription_amount_cents,
      });

      if (props.trainer.user_id) {
        setSelectedUser({
          value: props.trainer.user_id,
          label: props.trainer.user_email,
        });
      }

      setFavouriteVideos(props.trainer.favourite_videos || []);
      setFavouriteWorkouts(props.trainer.favourite_workouts || []);
      setFavouritePrograms(props.trainer.favourite_programs || []);
    }
  }, [props.trainer]);

  // Fetch subscribers when modal opens
  useEffect(() => {
    if (isOpen && props.trainer?.id) {
      setLoadingSubscribers(true);
      getSubscriptionsByTrainer(props.trainer.id)
        .then((res) => {
          setSubscribers(res.results || []);
        })
        .catch(() => {
          setSubscribers([]);
        })
        .finally(() => {
          setLoadingSubscribers(false);
        });
    }
  }, [isOpen, props.trainer?.id]);

  const validateForm = () => {
    if (
      touched["monthly_plays"] &&
      (!trainer.monthly_plays ||
        isNaN(trainer.monthly_plays) ||
        trainer.monthly_plays < 0)
    ) {
      toast.error(
        "Monthly plays must be a positive number and cannot be empty",
      );
      return false;
    }

    if (
      touched["followers"] &&
      (!trainer.followers || isNaN(trainer.followers) || trainer.followers < 0)
    ) {
      toast.error(
        "Total followers must be a positive number and cannot be empty",
      );
      return false;
    }

    const urlRegex = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;
    if (
      touched["social_ig"] &&
      trainer.social_ig &&
      !urlRegex.test(trainer.social_ig)
    ) {
      toast.error("Instagram URL is invalid");
      return false;
    }
    if (
      touched["social_tt"] &&
      trainer.social_tt &&
      !urlRegex.test(trainer.social_tt)
    ) {
      toast.error("TikTok URL is invalid");
      return false;
    }
    if (
      touched["social_fb"] &&
      trainer.social_fb &&
      !urlRegex.test(trainer.social_fb)
    ) {
      toast.error("Facebook URL is invalid");
      return false;
    }
    if (
      touched["social_tw"] &&
      trainer.social_tw &&
      !urlRegex.test(trainer.social_tw)
    ) {
      toast.error("X (Twitter) URL is invalid");
      return false;
    }

    if (
      touched["description"] &&
      trainer.description &&
      trainer.description.length > 500
    ) {
      toast.error("Bio must be less than 500 characters");
      return false;
    }

    return true;
  };

  const handleUserChange = (selectedOption: any) => {
    setSelectedUser(selectedOption);
    handleChange("user", selectedOption ? selectedOption.value : null);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setUpdate(true);

    const updatedTrainerData = {
      ...trainer,
      favourite_videos_ids: favouriteVideos.map((item) => item.id),
      favourite_workouts_ids: favouriteWorkouts.map((item) => item.id),
      favourite_programs_ids: favouritePrograms.map((item) => item.id),
    };

    const filteredTrainerData = Object.keys(updatedTrainerData)
      .filter((key) => touched[key] || key.includes("favourite"))
      .reduce((obj: { [key: string]: any }, key) => {
        obj[key] = updatedTrainerData[key];
        return obj;
      }, {});

    const update_trainer = await updateTrainer(
      filteredTrainerData,
      props.trainer?.id,
    );

    if (update_trainer.data.error) {
      toast.error(update_trainer.data.message);
      Sentry.captureMessage("Error updating athlete", {
        extra: {
          id: props.trainer?.id,
          payload: filteredTrainerData,
        },
      });
    } else {
      props.getTrainers();
      setOpen(false);
      toast.success("Trainer updated successfully!");
    }

    setUpdate(false);
  };

  const isModified = useMemo(() => {
    const trainerChanged = Object.keys(trainer).some(
      (key) => touched[key] && trainer[key] !== props.trainer[key],
    );

    const videosChanged =
      JSON.stringify(favouriteVideos) !==
      JSON.stringify(props.trainer.favourite_videos);

    const workoutsChanged =
      JSON.stringify(favouriteWorkouts) !==
      JSON.stringify(props.trainer.favourite_workouts);

    const programsChanged =
      JSON.stringify(favouritePrograms) !==
      JSON.stringify(props.trainer.favourite_programs);

    return (
      trainerChanged || videosChanged || workoutsChanged || programsChanged
    );
  }, [
    trainer,
    favouriteVideos,
    props.trainer,
    favouriteWorkouts,
    favouritePrograms,
    touched,
  ]);

  return (
    <>
      <div ref={rootRef}>
        <Button color="primary" onClick={() => setOpen(true)}>
          <div className="flex items-center gap-x-2">
            <HiOutlinePencilAlt className="text-lg" />
            Edit Athlete
          </div>
        </Button>
        <Modal
          onClose={() => setOpen(false)}
          show={isOpen}
          size="3xl"
          position="top-center"
          root={rootRef.current ?? undefined}
        >
          <ModalHeader className="border-b border-gray-200 p-6! dark:border-gray-700">
            <strong>Edit Athlete</strong>
          </ModalHeader>
          <ModalBody className="max-h-[70vh] overflow-y-auto">
            <div className="mb-1 block">
              <Label htmlFor="firstName">Bio</Label>
              <div className="mt-1">
                <Textarea
                  id="comment"
                  name="description"
                  required
                  rows={1}
                  defaultValue={props.trainer.description}
                  placeholder="Bio"
                  onChange={({ target: { name, value } }) =>
                    handleChange(name, value)
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 overflow-visible sm:grid-cols-2">
              <div>
                <Label htmlFor="firstName">Monthly Plays</Label>
                <div className="mt-1">
                  <TextInput
                    id="monthly_plays"
                    name="monthly_plays"
                    defaultValue={props.trainer.monthly_plays}
                    placeholder="Monthly Plays"
                    onChange={({ target: { name, value } }) =>
                      handleChange(name, value)
                    }
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="firstName">Total Followers</Label>
                <div className="mt-1">
                  <TextInput
                    id="followers"
                    name="followers"
                    defaultValue={props.trainer.total_followers}
                    placeholder="Followers"
                    onChange={({ target: { name, value } }) =>
                      handleChange(name, value)
                    }
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="firstName">Instagram</Label>
                <div className="mt-1">
                  <TextInput
                    id="instagram"
                    name="social_ig"
                    defaultValue={props.trainer.social_ig}
                    placeholder="Instagram"
                    onChange={({ target: { name, value } }) =>
                      handleChange(name, value)
                    }
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="firstName">TikTok</Label>
                <div className="mt-1">
                  <TextInput
                    id="tiktok"
                    name="social_tt"
                    defaultValue={props.trainer.social_tt}
                    placeholder="Tiktok"
                    onChange={({ target: { name, value } }) =>
                      handleChange(name, value)
                    }
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="firstName">Facebook</Label>
                <div className="mt-1">
                  <TextInput
                    id="facebook"
                    name="social_fb"
                    defaultValue={props.trainer.social_fb}
                    placeholder="Facebook"
                    onChange={({ target: { name, value } }) =>
                      handleChange(name, value)
                    }
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="firstName">X (formerly Twitter)</Label>
                <div className="mt-1">
                  <TextInput
                    id="x"
                    name="social_tw"
                    defaultValue={props.trainer.social_tw}
                    placeholder="X"
                    onChange={({ target: { name, value } }) =>
                      handleChange(name, value)
                    }
                  />
                </div>
              </div>

              <div>
                <Label>Profile picture</Label>
                <FileUploadZone
                  acceptedTypes={[
                    "image/jpg",
                    "image/png",
                    "image/jpeg",
                    "image/webp",
                  ]}
                  onImageChange={(image) => {
                    handleChange("profile_image", {
                      uri_data: image.dataUri,
                      file_type: image.type,
                      file_name: image.name,
                    });
                  }}
                  defaultPreview={{
                    url: props.trainer.profile_image,
                  }}
                  maxDimensions={{ width: 2000, height: 2000 }}
                  maxSize={10 * 1024 * 1024}
                />
              </div>

              <div>
                <Label>Cover Image</Label>
                <FileUploadZone
                  acceptedTypes={[
                    "image/jpg",
                    "image/png",
                    "image/jpeg",
                    "image/webp",
                  ]}
                  onImageChange={(image) => {
                    handleChange("cover_image", {
                      uri_data: image.dataUri,
                      file_type: image.type,
                      file_name: image.name,
                    });
                  }}
                  defaultPreview={{
                    url: props.trainer.cover_image,
                  }}
                  maxDimensions={{ width: 2000, height: 2000 }}
                  maxSize={10 * 1024 * 1024}
                />
              </div>

              <div>
                <Label>Thumb picture</Label>
                <FileUploadZone
                  acceptedTypes={[
                    "image/jpg",
                    "image/png",
                    "image/jpeg",
                    "image/webp",
                  ]}
                  onImageChange={(image) => {
                    handleChange("thumb_image", {
                      uri_data: image.dataUri,
                      file_type: image.type,
                      file_name: image.name,
                    });
                  }}
                  defaultPreview={{
                    url: props.trainer.thumb_image,
                  }}
                  maxDimensions={{ width: 2000, height: 2000 }}
                  maxSize={10 * 1024 * 1024}
                />
              </div>

              <div>
                <div className="mt-1 flex w-64 flex-col space-y-4 pb-4 pl-1">
                  <Label>Additional options</Label>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      name="status"
                      className="peer sr-only"
                      onChange={({ target: { name, checked } }) =>
                        handleChange(name, String(checked))
                      }
                      defaultChecked={props?.trainer?.status === 1}
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:size-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-hidden peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                    <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                      Status
                    </span>
                  </label>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      name="squad"
                      className="peer sr-only"
                      onChange={({ target: { name, checked } }) =>
                        handleChange(name, String(checked))
                      }
                      defaultChecked={props?.trainer?.squad}
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:size-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-hidden peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                    <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                      Squad
                    </span>
                  </label>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      name="featured"
                      className="peer sr-only"
                      onChange={({ target: { name, checked } }) =>
                        handleChange(name, String(checked))
                      }
                      defaultChecked={props?.trainer?.featured}
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:size-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-hidden peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                    <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                      Featured
                    </span>
                  </label>
                </div>
              </div>

              <div className="">
                <Label>Favorite Workouts</Label>
                <FavoriteTable
                  items={favouriteWorkouts}
                  onClearAll={() =>
                    handleFavoritesChange(FavoriteType.Workouts, [])
                  }
                  onRemove={(id: number) => {
                    handleFavoritesChange(
                      FavoriteType.Workouts,
                      favouriteWorkouts.filter((workout) => workout.id !== id),
                    );
                  }}
                  onSelect={(item: FavoriteItem) =>
                    handleFavoritesChange(
                      FavoriteType.Workouts,
                      favouriteWorkouts.concat(item),
                    )
                  }
                  type={FavoriteType.Workouts}
                />
              </div>
              <div className="">
                <Label>Favorite Videos</Label>
                <FavoriteTable
                  items={favouriteVideos}
                  onClearAll={() =>
                    handleFavoritesChange(FavoriteType.Videos, [])
                  }
                  onRemove={(id: number) => {
                    handleFavoritesChange(
                      FavoriteType.Videos,
                      favouriteVideos.filter((video) => video.id !== id),
                    );
                  }}
                  onSelect={(item: FavoriteItem) =>
                    handleFavoritesChange(
                      FavoriteType.Videos,
                      favouriteVideos.concat(item),
                    )
                  }
                  type={FavoriteType.Videos}
                />
              </div>
              <div className="">
                <Label>Favorite Programs</Label>
                <FavoriteTable
                  items={favouritePrograms}
                  onClearAll={() =>
                    handleFavoritesChange(FavoriteType.Programs, [])
                  }
                  onRemove={(id: number) => {
                    handleFavoritesChange(
                      FavoriteType.Programs,
                      favouritePrograms.filter((program) => program.id !== id),
                    );
                  }}
                  onSelect={(item: FavoriteItem) =>
                    handleFavoritesChange(
                      FavoriteType.Programs,
                      favouritePrograms.concat(item),
                    )
                  }
                  type={FavoriteType.Programs}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label>Associated User</Label>
                <UserSearchSelect
                  value={selectedUser}
                  onChange={handleUserChange}
                />
              </div>
            </div>

            {/* Subscription Configuration Section */}
            <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-700">
              <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-white">
                Subscription Configuration
                {props.trainer?.subscription_stripe_product_id && (
                  <a
                    href={`https://dashboard.stripe.com/products/${props.trainer.subscription_stripe_product_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-normal text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                    title="View Stripe Product"
                  >
                    <HiExternalLink className="size-4" />
                    Stripe
                  </a>
                )}
              </h3>
              <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                Configure paywall and subscription pricing for this athlete. If no price is set, all workouts are free.
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      name="paywall_enabled"
                      className="peer sr-only"
                      onChange={({ target: { name, checked } }) =>
                        handleChange(name, String(checked))
                      }
                      defaultChecked={props?.trainer?.paywall_enabled}
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:size-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-hidden peer-focus:ring-4 peer-focus:ring-blue-300 dark:border-gray-600 dark:bg-gray-700 dark:peer-focus:ring-blue-800"></div>
                    <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
                      Paywall Enabled
                    </span>
                  </label>
                </div>

                <div>
                  <Label>Subscription Price (USD)</Label>
                  <div className="mt-1">
                    <TextInput
                      id="subscription_amount"
                      name="subscription_amount_cents"
                      type="number"
                      min={0}
                      step={0.01}
                      defaultValue={
                        props.trainer.subscription_amount_cents
                          ? (props.trainer.subscription_amount_cents / 100).toFixed(2)
                          : ""
                      }
                      placeholder="e.g. 9.99"
                      onChange={({ target: { name, value } }) => {
                        const cents = value ? Math.round(parseFloat(value) * 100) : null;
                        handleChange(name, cents);
                      }}
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Leave empty for free access to all workouts.
                    </p>
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <Label>Subscription Description</Label>
                  <div className="mt-1">
                    <Textarea
                      id="subscription_description"
                      name="subscription_description"
                      rows={3}
                      defaultValue={props.trainer.subscription_description || ""}
                      placeholder="Describe what subscribers get access to..."
                      onChange={({ target: { name, value } }) =>
                        handleChange(name, value)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Subscribers Table */}
            <div className="mt-6 border-t border-gray-200 pt-6 dark:border-gray-700">
              <h3 className="mb-4 text-sm font-semibold text-gray-900 dark:text-white">
                Current Subscribers{" "}
                <span className="text-xs font-normal text-gray-500">
                  ({subscribers.length})
                </span>
              </h3>
              {loadingSubscribers ? (
                <p className="text-sm text-gray-500">Loading subscribers...</p>
              ) : subscribers.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No active subscribers yet.
                </p>
              ) : (
                <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-left text-sm">
                    <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-400">
                      <tr>
                        <th className="px-4 py-2">Name</th>
                        <th className="px-4 py-2">Status</th>
                        <th className="px-4 py-2">Subscribed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscribers.map((sub) => (
                        <tr
                          key={sub.id}
                          className="border-b border-gray-200 dark:border-gray-700"
                        >
                          <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                            {sub.user?.name || "—"}
                          </td>
                          <td className="px-4 py-2">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                sub.status === "ACTIVE"
                                  ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                  : sub.status === "CANCELLED"
                                    ? "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
                                    : "bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-300"
                              }`}
                            >
                              {sub.status}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
                            {new Date(sub.created_at).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              disabled={updating || !isModified}
              onClick={handleSubmit}
            >
              {updating ? "Updating..." : "Update"}
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
};

export default EditTrainerModal;
